import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompanyDirectoryService } from '@shared/services/company-directory.service';
import { firstValueFrom } from 'rxjs';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { WorkCycleConfig } from '../work-cycle/work-cycle.interface';
import { RestConfigurationConfig } from './rest-configuration.interface';

interface CompaniesRecordsResponse {
  items?: CompaniesRecord[];
  data?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

@Component({
  selector: 'app-rest-configuration',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule, MatCardModule],
  templateUrl: './rest-configuration.component.html',
  styleUrl: './rest-configuration.component.scss',
})
export class RestConfigurationComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly companyDirectory = inject(CompanyDirectoryService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  dataSource = new MatTableDataSource<RestConfigurationConfig>([]);
  allConfigurations: RestConfigurationConfig[] = [];
  companies: CompaniesRecord[] = [];
  workCycles: WorkCycleConfig[] = [];
  isLoading = true;
  actions: TableAction<RestConfigurationConfig>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<RestConfigurationConfig>[] = [];
  activeSearch = '';
  activeFilters: Record<string, string | number | boolean | null> = {};

  readonly modeOptions = ['fixed_weekday', 'cycle_reference'] as const;

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.configureTranslations();
        this.dataSource.data = this.dataSource.data.map((item) => this.normalizeConfiguration(item));
        this.buildTableFilters();
      });
    void this.loadData();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    try {
      const [records, companiesResponse, cycles] = await Promise.all([
        firstValueFrom(this.apiService.get<RestConfigurationConfig[]>('labor-config/rest-configurations')),
        this.companies.length
          ? Promise.resolve(this.companies as CompaniesRecord[] | CompaniesRecordsResponse)
          : this.companyDirectory.getAllActive(),
        firstValueFrom(this.apiService.get<WorkCycleConfig[]>('labor-config/work-cycles')),
      ]);

      this.companies = this.normalizeCompanies(companiesResponse)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
      this.workCycles = (cycles || []).filter((cycle) => cycle.isActive !== false);
      this.allConfigurations = (records || []).map((item) => this.normalizeConfiguration(item));
      this.buildTableFilters();
      this.applyTableFilters();
    } catch (error) {
      console.error('Error loading rest configurations', error);
      this.allConfigurations = [];
      this.companies = [];
      this.workCycles = [];
      this.tableFilters = [];
      this.dataSource.data = [];
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('restConfigurationsCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/attendance/rest-configurations/new']);
  }

  async handleEdit(row: RestConfigurationConfig): Promise<void> {
    if (!this.hasAccess('update')) {
      return;
    }
    await this.router.navigate(['/attendance/rest-configurations', row.restConfigurationId, 'edit']);
  }

  async handleView(row: RestConfigurationConfig): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/rest-configurations', row.restConfigurationId]);
  }

  handleDelete(row: RestConfigurationConfig): void {
    if (!this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('restConfigurationsCatalog.dialog.deleteTitle'),
        message: this.t('restConfigurationsCatalog.dialog.deleteMessage', { config: row.name }),
        confirmLabel: this.t('restConfigurationsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(
          this.apiService.delete(`labor-config/rest-configurations/${row.restConfigurationId}`)
        );
        await this.loadData();
        this.showNotification(
          'snackbar-success',
          this.t('restConfigurationsCatalog.notifications.deleted'),
          'bottom',
          'center'
        );
      } catch (error) {
        console.error('Error deleting rest configuration', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('restConfigurationsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: RestConfigurationConfig[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(
          rows.map((row) =>
            firstValueFrom(
              this.apiService.delete(`labor-config/rest-configurations/${row.restConfigurationId}`)
            )
          )
        );
        await this.loadData();
        this.showNotification(
          'snackbar-success',
          this.t('common.bulkDeleteSuccess', { count: rows.length }),
          'bottom',
          'center'
        );
      } catch (error) {
        console.error('Error deleting rest configurations', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('restConfigurationsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  onSearchChange(search: string): void {
    this.activeSearch = search.trim().toLowerCase();
    this.applyTableFilters();
  }

  onFiltersChange(filters: Record<string, string | number | boolean | null>): void {
    this.activeFilters = filters;
    this.applyTableFilters();
  }

  private normalizeConfiguration(
    item: Partial<RestConfigurationConfig>
  ): RestConfigurationConfig {
    const mode = typeof item.mode === 'string' && item.mode.trim() ? item.mode.trim() : 'fixed_weekday';
    const restDays = Array.isArray(item.restDays) ? item.restDays.map((value) => Number(value)) : [];
    const weekdays = Array.isArray(item.weekdays) ? item.weekdays.map((value) => Number(value)) : [];
    const cycleReference = typeof item.cycleReference === 'string' && item.cycleReference.trim()
      ? item.cycleReference.trim()
      : null;
    const isActive = item.isActive !== false;

    return {
      restConfigurationId: item.restConfigurationId || crypto.randomUUID(),
      center: item.center || '',
      companiesRecordId: item.companiesRecordId,
      name: item.name || '',
      mode,
      restDays,
      weekdays,
      cycleReference,
      isActive,
      companyName: this.getCompanyName(item.companiesRecordId),
      modeLabel: this.getModeLabel(mode),
      summaryLabel: this.getSummaryLabel(mode, weekdays, restDays, cycleReference),
      cycleReferenceLabel: this.getCycleName(cycleReference),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('restConfigurationsCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'restConfigurations', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'refresh',
        icon: 'refresh',
        tooltip: this.t('common.reload'),
        colorClass: 'neutral',
        auth: { resource: 'restConfigurations', action: 'read' },
        behavior: 'refresh',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('common.select'),
        colorClass: 'info',
        auth: { resource: 'restConfigurations', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('common.update'),
        colorClass: 'accent',
        auth: { resource: 'restConfigurations', action: 'update' },
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('common.delete'),
        colorClass: 'danger',
        auth: { resource: 'restConfigurations', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'name', label: this.t('restConfigurationsCatalog.fields.name'), type: 'text', visible: true },
      { def: 'companyName', label: this.t('orgUnitsCatalog.fields.company'), type: 'text', visible: true },
      { def: 'modeLabel', label: this.t('restConfigurationsCatalog.fields.mode'), type: 'text', visible: true },
      { def: 'summaryLabel', label: this.t('restConfigurationsCatalog.fields.summary'), type: 'text', visible: true },
      {
        def: 'activeLabel',
        label: this.t('restConfigurationsCatalog.fields.status'),
        type: 'status',
        visible: true,
        statusBadgeMap: {
          [this.t('common.status.active')]: 'badge badge-solid-green',
          [this.t('common.status.inactive')]: 'badge badge-solid-red',
        },
      },
    ];

    this.dataSource.filterPredicate = (item, filter) =>
      [item.name, item.companyName, item.modeLabel, item.summaryLabel, item.activeLabel]
        .join(' ')
        .toLowerCase()
        .includes(filter);
  }

  private buildTableFilters(): void {
    const companyOptions = this.companies.map((company) => ({
      value: company.companiesRecordId,
      label: company.name,
      searchText: `${company.code || ''} ${company.center || ''}`.trim(),
    }));

    this.tableFilters = [
      {
        id: 'company',
        label: this.t('orgUnitsCatalog.fields.company'),
        icon: 'business',
        value: this.activeFilters['company'] ?? null,
        searchable: true,
        options: companyOptions,
      },
      {
        id: 'status',
        label: this.t('restConfigurationsCatalog.fields.status'),
        icon: 'verified',
        searchable: true,
        value: this.activeFilters['status'] ?? null,
        options: [
          { value: 'active', label: this.t('common.status.active') },
          { value: 'inactive', label: this.t('common.status.inactive') },
        ],
      },
    ];
  }

  private applyTableFilters(): void {
    const searchTerm = this.activeSearch;
    this.dataSource.data = this.allConfigurations.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        [item.name, item.companyName, item.modeLabel, item.summaryLabel, item.activeLabel]
          .join(' ')
          .toLowerCase()
          .includes(searchTerm);

      const matchesCompany =
        !this.activeFilters['company'] || item.companiesRecordId === this.activeFilters['company'];

      const matchesStatus =
        !this.activeFilters['status'] ||
        (this.activeFilters['status'] === 'active' && item.isActive) ||
        (this.activeFilters['status'] === 'inactive' && !item.isActive);

      return matchesSearch && matchesCompany && matchesStatus;
    });
  }

  private getModeLabel(mode: string): string {
    if (this.modeOptions.includes(mode as (typeof this.modeOptions)[number])) {
      return this.t(`restConfigurationsCatalog.modeOptions.${mode}`);
    }
    return mode;
  }

  private getSummaryLabel(
    mode: string,
    weekdays: number[],
    restDays: number[],
    cycleReference: string | null
  ): string {
    if (mode === 'fixed_weekday') {
      if (!weekdays.length) {
        return this.t('restConfigurationsCatalog.labels.noWeekdays');
      }
      return weekdays
        .map((weekday) => this.t(`restConfigurationsCatalog.weekdayOptions.${weekday}`))
        .join(', ');
    }

    const daysLabel = restDays.length
      ? restDays.join(', ')
      : this.t('restConfigurationsCatalog.labels.noRestDays');
    const cycleLabel = this.getCycleName(cycleReference);
    return this.t('restConfigurationsCatalog.labels.cycleDaysSummary', {
      days: daysLabel,
      cycle: cycleLabel,
    });
  }

  private getCompanyName(companiesRecordId: string | null | undefined): string {
    if (!companiesRecordId) {
      return this.t('restConfigurationsCatalog.labels.noCompany');
    }
    return (
      this.companies.find((company) => company.companiesRecordId === companiesRecordId)?.name ||
      this.t('restConfigurationsCatalog.labels.noCompany')
    );
  }

  private getCycleName(workCycleId: string | null | undefined): string {
    if (!workCycleId) {
      return this.t('restConfigurationsCatalog.labels.noCycle');
    }
    return (
      this.workCycles.find((cycle) => cycle.workCycleId === workCycleId)?.name ||
      this.t('restConfigurationsCatalog.labels.noCycle')
    );
  }

  private normalizeCompanies(
    response: CompaniesRecord[] | CompaniesRecordsResponse | null | undefined
  ): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response?.items || response?.data || response?.results || response?.companiesRecords || [];
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'restConfigurations', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
